<script setup>
import { onMounted, ref } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import UserService from '@/services/UserService'
import FormButton from '@/components/Atoms/FormButton.vue'
import Alert from '@/components/Atoms/Alert.vue'
import FloatingInput from '@/components/ui/FloatingInput.vue'
import Spinner from '@/components/ui/Spinner.vue'
import { track, EVENTS } from '@/lib/tracking'

const { notify } = useNotification()

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const email = ref('')
const emailError = ref('')
const error = ref(null)
const requested = ref(false)
const processing = ref(false)
const emailInput = ref(null)

function clearEmailError() {
	emailError.value = ''
	error.value = null
}

function onEmailKeydown(event) {
	if (event.key === 'Enter') {
		event.preventDefault()
		requestReset()
	}
}

function onEmailBlur() {
	const value = (email.value || '').trim()
	if (value && !EMAIL_RE.test(value)) {
		emailError.value = 'Please enter a valid email.'
	}
}

function validateForm() {
	const value = (email.value || '').trim()
	if (!value) {
		emailError.value = 'Please enter your email address.'
		return false
	}
	if (!EMAIL_RE.test(value)) {
		emailError.value = 'Please enter a valid email.'
		return false
	}
	emailError.value = ''
	return true
}

async function requestReset() {
	if (processing.value) return

	error.value = null
	if (!validateForm()) {
		emailInput.value?.focus()
		return
	}

	processing.value = true

	try {
		await UserService.requestPasswordReset(email.value.trim())

		track(EVENTS.PASSWORD_RESET_REQUESTED, {})
		notify({
			group: 'info',
			title: 'Request Sent',
			text: 'We have sent you an email for further instructions',
		})
		requested.value = true
	} catch (err) {
		error.value = err
	}

	processing.value = false
}

onMounted(() => {
	document.title = 'Forgot password – Castr'
	emailInput.value?.focus()
})
</script>

<template>
	<div class="auth-page">
		<div class="auth-card">
			<div class="flex flex-col items-center text-center mb-7">
				<div class="auth-logo-wrapper">
					<div class="auth-logo-dot" aria-hidden="true" />
					<a href="https://castr.com" aria-label="Go to Castr home">
						<img class="auth-logo" src="@/assets/castr.svg" alt="Castr" />
					</a>
				</div>
			</div>

			<template v-if="requested">
				<div class="mb-6">
					<h1 class="auth-title">Check your email</h1>
					<p class="auth-subtitle">
						We sent password reset instructions to
						<span class="text-surface-text-1 font-medium">{{ email }}</span>.
					</p>
				</div>

				<Alert type="normal" class="mb-5 text-left">
					<div>
						<p class="mb-1 text-sm font-medium text-surface-9">Please check your mailbox</p>
						<p class="mb-0 text-tiny text-surface-7">
							If you don&apos;t see the email, check your spam folder.
						</p>
					</div>
				</Alert>

				<div class="flex justify-center">
					<FormButton
						type="main"
						size="lg"
						class="!h-10 !px-14 w-full sm:w-auto justify-center"
						to="/signin"
					>
						Sign in
					</FormButton>
				</div>
			</template>

			<template v-else>
				<div class="mb-6">
					<h1 class="auth-title">Forgot Password</h1>
					<p class="auth-subtitle">
						Enter your registered email address and we'll send you instructions to reset your password.
					</p>
				</div>

				<Alert
					v-if="error"
					type="error"
					:text="error.message"
					class="left inline mb-4"
				/>

				<form @submit.prevent="requestReset">
					<div class="mb-5">
						<FloatingInput
							ref="emailInput"
							id="forgot-password-email"
							label="Email address"
							hint="you@company.com"
							v-model="email"
							type="email"
							inputmode="email"
							autocomplete="email"
							:has-error="!!emailError"
							:helper="emailError"
							:disabled="processing"
							@update:modelValue="clearEmailError"
							@blur="onEmailBlur"
							@keydown="onEmailKeydown"
						/>
					</div>

					<div class="flex justify-center">
						<FormButton
							type="main"
							size="lg"
							class="!h-10 !px-14 w-full sm:w-auto justify-center"
							:disabled="processing"
							@click="requestReset"
						>
							<template v-if="processing">
								Requesting reset
								<Spinner
									text=""
									classes="ml-2"
									spinner-color="currentColor"
									spinner-size="14px"
								/>
							</template>
							<template v-else>Request reset</template>
						</FormButton>
					</div>
				</form>

				<p class="auth-footer">
					Remember your password?
					<router-link to="/signin" class="auth-link">Sign in</router-link>
				</p>
			</template>
		</div>
	</div>
</template>

<style scoped>
.auth-page {
	position: relative;
	width: 100%;
	min-height: 100vh;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 16px;
	gap: 18px;
}
.auth-page::before {
	content: '';
	position: absolute;
	inset: 0;
	background:
		radial-gradient(65% 45% at 50% 0%, rgba(0, 98, 255, 0.16), transparent 68%),
		radial-gradient(34% 42% at 50% 46%, rgba(0, 98, 255, 0.07), transparent 72%);
	pointer-events: none;
}
.auth-page > * {
	position: relative;
}
.auth-card {
	width: 100%;
	max-width: 384px;
	background-color: var(--castr-surface);
	border: 1px solid var(--castr-border);
	border-radius: 16px;
	box-shadow: 0 24px 60px -24px rgba(0, 0, 0, 0.9);
	padding: 40px 28px 32px;
}
@media (min-width: 640px) {
	.auth-card {
		padding: 40px 36px 32px;
	}
}
.auth-logo {
	width: 120px;
	height: 40px;
}
.auth-logo-wrapper {
	position: relative;
	display: inline-block;
	line-height: 0;
}
.auth-logo-dot {
	position: absolute;
	width: 4px;
	height: 4px;
	border-radius: 50%;
	left: 116px;
	top: 12px;
	background-color: #ef0000;
	animation: blink 2s ease-in-out infinite;
}
.auth-title {
	font-family: var(--font-display);
	font-size: 24px;
	line-height: 1.15;
	font-weight: 700;
	letter-spacing: -0.02em;
	color: #fff;
	text-align: center;
	margin-bottom: 6px;
}
.auth-subtitle {
	font-size: 14px;
	line-height: 1.5;
	color: var(--castr-text-muted);
	text-align: center;
}
.auth-link {
	color: var(--castr-blue-soft);
	font-weight: 600;
}
.auth-link:hover {
	text-decoration: underline;
}
.auth-footer {
	margin-top: 24px;
	text-align: center;
	font-size: 14px;
	color: var(--castr-text-muted);
}
.auth-card :deep(button.form-button) {
	font-size: 14px;
	font-weight: 600;
	letter-spacing: 0.01em;
	border-radius: 12px !important;
}
.auth-card :deep(button.bg-main:not(:disabled)) {
	box-shadow: 0 10px 30px -8px rgba(0, 98, 255, 0.5);
	transition:
		box-shadow 0.2s ease,
		transform 0.12s ease,
		background-color 0.15s ease;
}
.auth-card :deep(button.bg-main:not(:disabled):hover) {
	transform: translateY(-1px);
	box-shadow: 0 14px 36px -8px rgba(0, 98, 255, 0.6);
}
.auth-card :deep(button.bg-main:not(:disabled):active) {
	transform: translateY(0);
	box-shadow: 0 6px 18px -8px rgba(0, 98, 255, 0.5);
}
.auth-card :deep(button:disabled) {
	background-color: rgba(255, 255, 255, 0.06) !important;
	color: var(--castr-text-muted) !important;
	cursor: not-allowed;
	box-shadow: none;
}
</style>

<style>
body:has(.auth-page),
body:has(.auth-page) #app {
	background-color: var(--castr-bg);
}
body:has(.auth-page) .footer span {
	color: var(--castr-text-muted);
}
</style>
